import { z } from 'zod';

const AutoReloadSchema = z.object({
  value: z.boolean().catch(false),
  threshold: z.number().catch(0),
  amount: z.number().catch(0),
  secondary_threshold: z.number().catch(0),
  secondary_amount: z.number().catch(0),
});

const GoogleSheetSchema = z.object({
  _id: z.string().catch(''),
  type: z.string().catch(''),
  sheet_id: z.string().catch(''),
});

const TaxInformationSchema = z.object({
  marketplace_region_id: z.string().catch(''),
  tax_field: z.string().catch(''),
  tax_value: z.string().catch(''),
});

const AddressSchema = z.object({
  address_line_one: z.string().catch(''),
  address_line_two: z.string().catch(''),
  city: z.string().catch(''),
  state: z.string().catch(''),
  zip_code: z.string().catch(''),
  country: z.string().catch(''),
});

const LogoSchema = z.object({
  key: z.string().catch(''),
  bucket_name: z.string().catch(''),
});

const OnBoardingSchema = z.object({
  checklist: z.array(z.string()).catch([]),
});

const LastLoginSchema = z.object({
  ip: z.string().catch(''),
  user_id: z.string().catch(''),
  timestamp: z.string().catch(''),
});

const LabelSettingsSchema = z.object({
  use_fulfillment_provider_name: z.boolean().catch(false),
});

const RepresentativeContactSchema = z.object({
  last_name: z.string().catch(''),
  first_name: z.string().catch(''),
  phone: z.string().catch(''),
  email: z.string().catch(''),
});

const DraftPurchaseOrderSchema = z.object({
  _id: z.string().catch(''),
});

const LatestPurchaseOrderSchema = z.object({
  _id: z.string().catch(''),
  email_addresses: z.array(z.string()).catch([]),
});

export const POMetadataSupplierSchema = z.object({
  _id: z.string().catch(''),
  organization_id: z.string().catch(''),
  supplier_name: z.string().catch(''),
  email: z.string().optional(),
  lead_time: z.number().optional(),
  representative_contact: RepresentativeContactSchema.optional(),
  draft_purchase_orders: z.array(DraftPurchaseOrderSchema).catch([]),
  latest_purchase_order: LatestPurchaseOrderSchema.optional(),
});

export const POMetadataOrganizationSchema = z.object({
  _id: z.string().catch(''),
  auto_reload: AutoReloadSchema.catch({
    value: false,
    threshold: 0,
    amount: 0,
    secondary_threshold: 0,
    secondary_amount: 0,
  }),
  catalog_health: z.unknown().nullable().catch(null),
  email_list: z.array(z.string()).catch([]),
  paused: z.boolean().catch(false),
  suspended: z.boolean().catch(false),
  flagged: z.boolean().catch(false),
  deleted: z.boolean().catch(false),
  company_name: z.string().catch(''),
  email: z.string().catch(''),
  on_boarding: OnBoardingSchema.catch({ checklist: [] }),
  google_sheets: z.array(GoogleSheetSchema).catch([]),
  tax_information: z.array(TaxInformationSchema).catch([]),
  createdAt: z.string().catch(''),
  updatedAt: z.string().catch(''),
  __v: z.number().catch(0),
  default_marketplace_region_id: z.string().catch(''),
  last_login: LastLoginSchema.optional(),
  address: AddressSchema.catch({
    address_line_one: '',
    address_line_two: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  }),
  seed_disabled: z.boolean().catch(false),
  display_id: z.string().catch(''),
  suppliers: z.array(POMetadataSupplierSchema).catch([]),
  logo: LogoSchema.optional(),
  phone: z.string().optional(),
  label_settings: LabelSettingsSchema.optional(),
});

export const POMetadataResponseSchema = z.object({
  data: POMetadataOrganizationSchema,
  requestId: z.string().optional(),
});

export type POMetadataSupplier = z.infer<typeof POMetadataSupplierSchema>;
export type POMetadataOrganization = z.infer<
  typeof POMetadataOrganizationSchema
>;
export type POMetadataResponse = z.infer<typeof POMetadataResponseSchema>;
